import React, { useState, useEffect, useRef } from 'react';
import { X, Search, AlertTriangle, Loader2, Check, User, Building } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ManualPostulationModal({ open, onClose, token, onSuccess, convocatorias }) {
  const [convocatoriaId, setConvocatoriaId] = useState('');
  const [tipoPostulante, setTipoPostulante] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // User search
  const [searchInput, setSearchInput] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setConvocatoriaId('');
      setTipoPostulante('');
      setPin('');
      setSelectedUser(null);
      setSearchInput('');
      setResults([]);
      setShowResults(false);
    }
  }, [open]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchInput || searchInput.trim().length < 2) {
      setResults([]);
      return;
    }
    if (selectedUser && (selectedUser.email === searchInput || selectedUser.nombre === searchInput)) {
      return;
    }
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API_URL}/api/users/search?q=${encodeURIComponent(searchInput.trim())}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (r.ok) {
          const data = await r.json();
          setResults(data.results || []);
          setShowResults(true);
        }
      } catch {
        // ignore
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [searchInput, token, selectedUser]);

  if (!open) return null;

  const handlePick = (u) => {
    setSelectedUser(u);
    setSearchInput(u.nombre || u.email);
    setShowResults(false);
    if (!tipoPostulante) {
      setTipoPostulante(u.user_type === 'juridica' ? 'empresa' : 'persona');
    }
  };

  const handleClear = () => {
    setSelectedUser(null);
    setSearchInput('');
    setResults([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!convocatoriaId) { toast.error('Seleccione una convocatoria'); return; }
    if (!selectedUser) { toast.error('Seleccione un usuario'); return; }
    if (!pin || pin.length !== 5) { toast.error('Ingrese el PIN de confirmación (5 dígitos)'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/postulaciones/manual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          convocatoria_id: convocatoriaId,
          user_id: selectedUser.id,
          pin,
          tipo_postulante: tipoPostulante || (selectedUser.user_type === 'juridica' ? 'empresa' : 'persona')
        })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data.detail || 'Error al postular');
      }
      toast.success(`Postulación creada: ${data.user?.nombre} a ${data.convocatoria?.codigo}`);
      if (onSuccess) onSuccess(data);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Error al crear postulación');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="manual-postulation-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">Postular Manualmente</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Aviso */}
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-3 flex gap-2 text-sm">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-900">Acción auditada</p>
              <p className="text-amber-800">Esta postulación quedará registrada en los logs con su email y la confirmación del PIN.</p>
            </div>
          </div>

          {/* Convocatoria */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Convocatoria *</label>
            <select
              value={convocatoriaId}
              onChange={(e) => setConvocatoriaId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
              data-testid="manual-post-convocatoria-select"
            >
              <option value="">Seleccione...</option>
              {(convocatorias || []).map(c => (
                <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
              ))}
            </select>
          </div>

          {/* User search */}
          <div className="space-y-1 relative">
            <label className="text-sm font-medium text-slate-700">Usuario a postular *</label>
            {selectedUser ? (
              <div className="flex items-center justify-between border border-green-400 bg-green-50 rounded-lg px-3 py-2" data-testid="manual-post-selected-user">
                <div className="flex items-center gap-2">
                  {selectedUser.user_type === 'juridica' ? <Building className="h-4 w-4 text-green-700" /> : <User className="h-4 w-4 text-green-700" />}
                  <div>
                    <div className="font-medium text-sm">{selectedUser.nombre}</div>
                    <div className="text-xs text-slate-600">{selectedUser.email} · {selectedUser.documento}</div>
                  </div>
                </div>
                <button type="button" onClick={handleClear} className="text-xs text-red-600 hover:underline">Cambiar</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Buscar por nombre, email, documento..."
                    className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="manual-post-search-input"
                    autoComplete="off"
                  />
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
                </div>
                {showResults && results.length > 0 && (
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto" data-testid="manual-post-search-results">
                    {results.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => handlePick(u)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0 flex items-center gap-2"
                        data-testid={`manual-post-result-${u.id}`}
                      >
                        {u.user_type === 'juridica' ? <Building className="h-4 w-4 text-blue-600" /> : <User className="h-4 w-4 text-blue-600" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">{u.nombre}</div>
                          <div className="text-xs text-slate-500 truncate">{u.email} {u.documento ? `· ${u.documento}` : ''}</div>
                        </div>
                        <span className="text-xs px-2 py-0.5 bg-slate-100 rounded">{u.user_type === 'juridica' ? 'Jurídica' : 'Natural'}</span>
                      </button>
                    ))}
                  </div>
                )}
                {showResults && results.length === 0 && !searching && searchInput.trim().length >= 2 && (
                  <div className="text-sm text-slate-500 mt-1 px-2">Sin resultados</div>
                )}
              </>
            )}
          </div>

          {/* Tipo Postulante */}
          {selectedUser && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700">Tipo de Postulante</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setTipoPostulante('persona')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm ${tipoPostulante === 'persona' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300 hover:bg-slate-50'}`}
                  data-testid="manual-post-tipo-persona"
                >
                  Persona Natural
                </button>
                <button
                  type="button"
                  onClick={() => setTipoPostulante('empresa')}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm ${tipoPostulante === 'empresa' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-300 hover:bg-slate-50'}`}
                  data-testid="manual-post-tipo-empresa"
                >
                  Empresa (Jurídica)
                </button>
              </div>
            </div>
          )}

          {/* PIN */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">PIN de Confirmación (5 dígitos) *</label>
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]{5}"
              maxLength={5}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
              placeholder="•••••"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 tracking-widest text-center text-lg"
              required
              data-testid="manual-post-pin-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300"
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedUser || !convocatoriaId || pin.length !== 5}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="manual-post-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {submitting ? 'Postulando...' : 'Confirmar Postulación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
